import {
    BodyText,
    Card,
    ColorPicker,
    ConditionalWrapper,
    Form,
    Icon,
    Popover,
    PopoverProps,
    Stack,
    Tag,
    isDark,
} from '@servicetitan/design-system';
import classNames from 'classnames';
import { FieldState } from 'formstate';
import { action, makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react';
import { Component, Fragment, RefObject, createRef } from 'react';
import { getColorPalette } from 'utils';
import * as Styles from './color-selector.module.less';

interface Props {
    label: string;
    color: FieldState<string>;
    name?: FieldState<string>;
    colorList?: string[];
    direction?: PopoverProps['direction'];
    /** Use if renders inside a form. Defaults to true */
    useForm?: boolean;
    disabled?: boolean;
}

@observer
export class ColorSelector extends Component<Props> {
    @observable isOpen = false;

    private ref = createRef<HTMLDivElement>();

    constructor(props: Props) {
        super(props);
        makeObservable(this);
    }

    componentDidMount() {
        document.addEventListener('mousedown', this.handleClickOutside);
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this.handleClickOutside);
    }

    setRef = (ref: RefObject<HTMLDivElement>) => {
        this.ref = ref;
    };

    @action
    openPopover = () => {
        this.isOpen = true;
    };

    @action
    handleClickOutside = (event: Event) => {
        if (this.ref.current && !this.ref.current.contains(event.target as Node)) {
            this.isOpen = false;
        }
    };

    render() {
        const { name, color, label, direction = 'r', useForm = true, ...restProps } = this.props;

        const trigger = (
            <div
                style={{ background: color.value, borderColor: color.value }}
                onClick={this.props.disabled ? undefined : this.openPopover}
                className={classNames(
                    Styles.colorSwatch,
                    Styles.colorSwatchDarkBorder,
                    'm-r-0',
                    'p-r-0',
                    'm-b-0-i',
                    { 'cursor-not-allowed-i': this.props.disabled }
                )}
            >
                <Stack
                    alignItems="center"
                    justifyContent="center"
                    direction="column"
                    className={classNames('w-100 h-100', {
                        [Styles.swatchOverlay]: !this.props.disabled,
                    })}
                >
                    <Icon
                        name={this.props.disabled ? 'do_not_disturb_alt' : 'edit'}
                        className={
                            this.props.disabled
                                ? isDark(color.value)
                                    ? 'c-white'
                                    : 'c-neutral-200'
                                : 'c-blue-400'
                        }
                        size={this.props.disabled ? 24 : 18}
                    />
                </Stack>
            </div>
        );
        return !this.isOpen ? (
            <div className={Styles.positionRelative}>{trigger}</div>
        ) : (
            <div ref={this.ref}>
                <Popover
                    trigger={trigger}
                    open
                    direction={direction}
                    width="s"
                    padding="m"
                    className={Styles.tagsPopover}
                    {...restProps}
                >
                    <BodyText>{label}</BodyText>
                    <Card padding="thin">
                        <ConditionalWrapper
                            condition={useForm}
                            wrapper={children => <Form>{children}</Form>}
                        >
                            <ColorPicker
                                colors={this.props.colorList || getColorPalette()}
                                label={label}
                                value={color.value.toLowerCase()}
                                onChange={color.onChange}
                            />
                        </ConditionalWrapper>
                    </Card>
                    {name?.value.length && (
                        <Fragment>
                            <BodyText size="small" className="m-t-1-i">
                                Preview
                            </BodyText>
                            <Card light padding="none">
                                <Tag color={color.value} className="m-1-i">
                                    {name.value}
                                </Tag>
                            </Card>
                        </Fragment>
                    )}
                </Popover>
            </div>
        );
    }
}
